// src/components/TrainModelModal.tsx
import React, { useEffect, useMemo } from "react";
import { Modal, Form, Input, Select, InputNumber, Collapse, Checkbox } from "antd";

type DatasetOption = { value: number; label: string };
type DatasetLite = { id: number; name: string,dataset_version_id?: number | null };

type Props = {
  open: boolean;
  datasetOptions: DatasetOption[];
  initialDataset?: DatasetLite | null; // if provided, dataset is locked
  confirmLoading?: boolean;
  onCancel: () => void;
  onSubmit: (form: {
    dataset_id: number;
    dataset_version_id?: number;
    model_name: string;
    arch: string;
    epochs: number;
    batch_size: number;
    learning_rate: number;
    augmentations?: string[];
    notes?: string;
  }) => void;
};

const defaultArchs = [
  { value: "yolo11n", label: "YOLOv11" },
//   { value: "yolov5", label: "YOLOv5" },
//   { value: "sam2", label: "SAM 2" },
//   { value: "resnet50", label: "ResNet50" },
];

const TrainModelModal: React.FC<Props> = ({
  open,
  datasetOptions,
  initialDataset,
  confirmLoading,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();

  const suggestedName = useMemo(() => {
    const base = initialDataset?.name || "New Model";
    const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16);
    return `${base} ${stamp}`;
  }, [initialDataset]);

  useEffect(() => {
    form.setFieldsValue({
      dataset_id: initialDataset?.id ?? undefined,
      model_name: suggestedName,
      arch: "yolo11n",
      epochs: 100,
      batch_size: 8,
      learning_rate: 0.001,
      augmentations: ["flip_h", "flip_v"],
      notes: "",
    });
  }, [form, initialDataset, suggestedName]);

  return (
    <Modal
      open={open}
      title="Train Model"
      okText="Start Training"
      onOk={() => form.submit()}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      width={640}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(vals) => onSubmit(vals)}
      >
        {initialDataset ? (
          <>
            <Form.Item label="Dataset">
              <Input disabled value={`${initialDataset.name} (#${initialDataset.id}_${initialDataset.dataset_version_id})`} />
            </Form.Item>
            {/* keep dataset_id in the payload */}
            <Form.Item name="dataset_id" hidden initialValue={initialDataset.id}>
              <Input />
            </Form.Item>
            <Form.Item name="dataset_version_id" hidden initialValue={initialDataset.dataset_version_id}>
              <Input />
            </Form.Item>
          </>
        ) : (
          <Form.Item
            label="Dataset"
            name="dataset_id"
            rules={[{ required: true, message: "Select a dataset" }]}
          >
            <Select
              showSearch
              placeholder="Select dataset"
              options={datasetOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        )}

        <Form.Item
          label="Model name"
          name="model_name"
          rules={[{ required: true, message: "Enter a model name" }, { max: 120 }]}
        >
          <Input placeholder="Ex. Bushing Detector 2025-08-18 11-12" />
        </Form.Item>

        <Form.Item label="Architecture" name="arch" rules={[{ required: true }]}>
          <Select options={defaultArchs} />
        </Form.Item>

        <div className="grid grid-cols-3 gap-4">
          <Form.Item label="Epochs" name="epochs" rules={[{ required: true }]}>
            <InputNumber min={1} max={1000} step={1} className="w-full" />
          </Form.Item>
          <Form.Item label="Batch size" name="batch_size" rules={[{ required: true }]}>
            <InputNumber min={1} max={1024} step={1} className="w-full" />
          </Form.Item>
          <Form.Item label="Learning rate" name="learning_rate" rules={[{ required: true }]}>
            <InputNumber min={1e-6} max={1} step={0.0001} stringMode className="w-full" />
          </Form.Item>
        </div>

        <Collapse
          items={[
            {
              key: "aug",
              label: "Augmentations",
              children: (
                <Form.Item name="augmentations" valuePropName="checkedValues" noStyle>
                  <Checkbox.Group
                    options={[
                      { value: "flip_h", label: "Flip H" },
                      { value: "flip_v", label: "Flip V" },
                      { value: "rotate", label: "Rotate" },
                      { value: "color", label: "Color jitter" },
                      { value: "blur", label: "Gaussian blur" },
                    ]}
                  />
                </Form.Item>
              ),
            },
            {
              key: "notes",
              label: "Notes",
              children: (
                <Form.Item name="notes" noStyle>
                  <Input.TextArea rows={3} placeholder="Optional notes" />
                </Form.Item>
              ),
            },
          ]}
          size="small"
          className="mb-2"
        />
      </Form>
    </Modal>
  );
};

export default TrainModelModal;
